import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation } from "swiper";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import LightboxImage from "./LightboxImage";
import SliderArrows from "../Slider/SliderArrows";

const fadeOverlay = {
	initial: {
		opacity: 0,
	},
	animate: {
		opacity: 0.9,
		transition: {
			duration: 0.1,
		},
	},
	exit: {
		opacity: 0,
	},
};
const fadeSwiper = {
	initial: {
		opacity: 0,
	},
	animate: {
		opacity: 1,
		transition: {
			delay: 0.05,
			duration: 0.2,
		},
	},
	exit: {
		opacity: 0,
	},
};

const Lightbox = ({ show, images, activeIndex, handleClose }) => {
	const closeButtonRef = useRef(null);
	const swiperRef = useRef(null);

	const handleNextSlide = () => {
		const swiper = swiperRef.current;
		if (!swiper) return;

		if (swiper.isEnd) {
			closeButtonRef.current?.focus();
			return;
		}

		const willReachLastSlide = swiper.activeIndex >= images.length - 2;
		swiper.slideNext();

		if (willReachLastSlide) {
			requestAnimationFrame(() => {
				closeButtonRef.current?.focus();
			});
		}
	};

	// keypress event listener - press escape to close lightbox
	useEffect(() => {
		const handleKeyPressClose = (e) => {
			if (e.key === "Escape") handleClose();
		};
		document.addEventListener("keydown", handleKeyPressClose);

		return () => document.removeEventListener("keydown", handleKeyPressClose);
	}, []);

	return (
		<AnimatePresence>
			{show && (
				<motion.div {...fadeSwiper} className="lightbox">
					<motion.span
						{...fadeOverlay}
						className="lightbox__overlay"
					></motion.span>
					<Swiper
						initialSlide={activeIndex}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
						}}
						navigation={{
							nextEl: ".swiper-button-next",
							prevEl: ".swiper-button-prev",
						}}
						modules={[Navigation, A11y]}
					>
						{images.map((image, index) => {
							return (
								<SwiperSlide onClick={handleClose} key={`lightbox-${index}`}>
									<LightboxImage image={image} />
								</SwiperSlide>
							);
						})}
					</Swiper>
					<button
						autoFocus
						ref={closeButtonRef}
						onClick={handleClose}
						className="close-btn"
						aria-label="close image lightbox"
					></button>
					<SliderArrows onNext={handleNextSlide} />
				</motion.div>
			)}
		</AnimatePresence>
	);
};

Lightbox.propTypes = {
	show: PropTypes.bool.isRequired,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			alt: PropTypes.string,
			caption: PropTypes.string,
		}).isRequired
	).isRequired,
	activeIndex: PropTypes.number,
	handleClose: PropTypes.func,
};

export default Lightbox;
